import { format, subDays } from "date-fns";
import {
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  Layers,
  Loader2,
  Receipt,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import type { SingleValue } from "react-select";

import { Load } from "components/load";
import PageError from "components/page-error";
import { PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import { SelectInput } from "components/Select";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import {
  useExportDepositExcelMutation,
  useExportDepositPdfMutation,
  useGetDepositAggregationsQuery,
} from "@/services/deposit-reconciliation";
import { toast } from "@/components/ui/use-toast";
import { layoutClasses } from "@/utils/css-classes";
import { downloadFromBlob } from "@/utils/download-blob";
import { formatToBRL } from "@/utils/formatToMoney";
import { formatOptionLabel } from "@/utils/format-option-label";

import { MESSAGES, TEXTS } from "../../utils/messages";
import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import { isValidPeriod } from "@/utils/date-range";
import AggregationByBank from "./components/aggregation-by-bank";
import AggregationByStore from "./components/aggregation-by-store";
import AggregationByAnalyst from "./components/aggregation-by-analyst";
import KpiCards from "./components/kpi-cards";
import PreviewTable from "./components/preview-table";
import { EXPORT_FORMATS, type ExportKind } from "./types";

export default function DepositReportsPage() {
  const [periodStart, setPeriodStart] = useState(
    format(subDays(new Date(), 30), "yyyy-MM-dd"),
  );
  const [periodEnd, setPeriodEnd] = useState(format(new Date(), "yyyy-MM-dd"));
  const [storeCode, setStoreCode] = useState<number | undefined>(undefined);

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const periodValid: boolean = isValidPeriod(periodStart, periodEnd);

  const { data, isLoading, isFetching, error } = useGetDepositAggregationsQuery(
    {
      periodStart,
      periodEnd,
      storeCodes: storeCode ? [storeCode] : undefined,
    },
    { refetchOnFocus: false, skip: !periodValid },
  );

  const [exportExcel, { isLoading: exportingExcel }] =
    useExportDepositExcelMutation();
  const [exportPdf, { isLoading: exportingPdf }] = useExportDepositPdfMutation();

  const handleExport = async (kind: ExportKind) => {
    const { extension, label } = EXPORT_FORMATS[kind];
    try {
      const params = {
        periodStart,
        periodEnd,
        storeCodes: storeCode ? [storeCode] : undefined,
      };
      const result =
        kind === "excel"
          ? await exportExcel(params).unwrap()
          : await exportPdf(params).unwrap();

      if (!(result instanceof Blob) || result.size === 0) {
        toast(MESSAGES.EXPORT.EMPTY);
        return;
      }

      downloadFromBlob(
        result,
        `conciliacao-depositos-${periodStart}-a-${periodEnd}.${extension}`,
      );
      toast(MESSAGES.EXPORT.SUCCESS(extension));
    } catch {
      toast(MESSAGES.EXPORT.ERROR(label));
    }
  };

  const currentStoreOption =
    (storeOptions ?? []).find(
      (o: { value: string }) => Number(o.value) === storeCode,
    ) ?? null;

  if (error)
    return (
      <PageError status={500} message={TEXTS.COMMON.INTERNAL_SERVER_ERROR} />
    );

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <PageTitle
            title="Relatórios de Depósitos"
            description="Dashboard agregado e exportações"
            titleSize="text-3xl"
            descriptionSize="text-xl"
          />
          <div className="flex flex-col lg:flex-row gap-2 items-end">
            <div className="w-64">
              <label className="text-xs text-slate-500 mb-1 block">Loja</label>
              <SelectInput
                options={storeOptions ?? []}
                value={currentStoreOption}
                onChange={newValue => {
                  const opt = newValue as SingleValue<{ value: string }>;
                  setStoreCode(opt?.value ? Number(opt.value) : undefined);
                }}
                isClearable
                classNamePrefix="an-simple-select"
                placeholder="Todas as lojas"
                formatOptionLabel={formatOptionLabel}
              />
            </div>
            <PeriodRangeFilter
              start={periodStart}
              end={periodEnd}
              onChange={(start, end) => {
                if (start) setPeriodStart(start);
                if (end) setPeriodEnd(end);
              }}
            />
          </div>
        </div>

        {isLoading && !data ? (
          <Load />
        ) : data ? (
          <div className="relative">
            {isFetching && (
              <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60 pt-10">
                <Loader2 className="h-8 w-8 animate-spin text-[#F4C83E]" />
              </div>
            )}
            <KpiCards
              cards={[
                {
                  label: "Σ Conciliado",
                  value: formatToBRL(data.kpis.totalConciliated),
                  Icon: Receipt,
                  tone: "primary",
                },
                {
                  label: "Conciliações Ativas",
                  value: data.kpis.activeBatchesCount,
                  Icon: CheckCircle2,
                  tone: "ok",
                },
                {
                  label: "Conciliações Desfeitas",
                  value: data.kpis.undoneBatchesCount,
                  Icon: XCircle,
                  tone: "warn",
                },
                {
                  label: "NSUs conciliadas",
                  value: data.kpis.nsuTotalCount,
                  Icon: Layers,
                  tone: "neutral",
                },
              ]}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              <AggregationByBank rows={data.byBank} />
              <AggregationByStore rows={data.byStore} />
              <AggregationByAnalyst rows={data.byAnalyst} />
            </div>

            <div className="mt-4">
              <PreviewTable rows={data.preview} />
            </div>

            <div className="mt-4 flex flex-col lg:flex-row gap-3 justify-end">
              <Button
                onClick={() => handleExport("excel")}
                disabled={exportingExcel || isFetching}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {exportingExcel ? "Gerando…" : "Baixar Excel (.xlsx)"}
              </Button>
              <Button
                onClick={() => handleExport("pdf")}
                disabled={exportingPdf || isFetching}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                <FileText className="w-4 h-4 mr-2" />
                {exportingPdf ? "Gerando…" : "Baixar PDF"}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
